import {
  normalizeCompanyData, normalizeCompanySpecialities, normalizeCompanyYear,
  normalizeEmployeeCountRange, positiveCompanyInteger,
} from "../utils/companyData.js";
import { readSearchField } from "./profileLocation.js";

export const COMPANY_SEARCH_ATTRIBUTES = [
  "company.id", "company.industry", "company.employee_count",
  "company.follower_count", "company.founded_on", "company.tagline",
  "company.description", "company.ln_url", "company.rc_id",
  "company.employee_count_range", "company.specialities",
];

const text = (...values) => values.find((value) => typeof value === "string" && value.trim())?.trim() || "";
const key = (value) => String(value || "").normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/&/g, " and ")
  .replace(/[^\p{L}\p{N}]+/gu, " ").trim().replace(/\s+/g, " ");

// Legacy enrichment stored unknown counts as zero. Never use followers as headcount.
export function readCompanyMetadata(record = {}) {
  const details = record.companyDetails || {};
  const field = (name) => readSearchField(record, `company.${name}`);
  const current = record.currentPosition?.company || {};
  const ids = normalizeCompanyData({
    id: field("id") || details.id || record.currentCompanyId || current.id,
    linkedinId: field("linkedinId") || field("linkedInId") || current.linkedinId || current.linkedInId,
    rc_id: field("rc_id") || field("rcId") || details.rcId || current.rc_id,
  });
  return {
    id: ids.id,
    rcId: ids.rc_id,
    name: text(field("name"), field("companyName"), details.name, record.currentCompanyName, record.currentPosition?.companyName,
      current.name, typeof record.company === "string" ? record.company : "", readSearchField(record, "profile.affiliation_name")),
    industry: text(field("industry"), details.industry, record.currentCompanyIndustry, current.industry),
    employeeCount: positiveCompanyInteger(field("employee_count"), field("employeeCount"), field("employeesCount"), details.employeeCount,
      record.currentCompanyEmployeeCount, current.employeeCount, current.employeesCount),
    employeeCountRange: normalizeEmployeeCountRange(field("employee_count_range") ?? field("employeeCountRange") ??
      details.employeeCountRange ?? current.employeeCountRange ?? current.employee_count_range),
    specialities: normalizeCompanySpecialities(field("specialities") ?? field("specialties") ??
      details.specialities ?? current.specialities ?? current.specialties),
    followerCount: positiveCompanyInteger(field("follower_count"), field("followerCount"), field("followersCount"), details.followerCount),
    foundedOn: normalizeCompanyYear(field("founded_on") ?? field("foundedOn.year") ?? field("foundedOn") ?? details.foundedOn),
    tagline: text(field("tagline"), details.tagline, record.currentCompanyTagline, current.tagline),
    description: text(field("description"), details.description, record.currentCompanyDescription, current.description),
    linkedinUrl: text(field("ln_url"), field("linkedinUrl"), field("linkedInUrl"), details.linkedinUrl, record.currentPosition?.companyUrl,
      record.experience?.[0]?.companyUrl, current.linkedinUrl, record.companyLinkedinUrl),
    websiteUrl: text(field("url"), details.websiteUrl, record.websiteUrl),
    identityConflict: details.identityConflict === true,
  };
}

export function mergeCompanyMetadata(record, incoming) {
  const existing = readCompanyMetadata(record);
  // A stale search record must not borrow facts from a different current employer.
  if ((existing.id && incoming.id && existing.id !== incoming.id) ||
      (existing.rcId && incoming.rcId && existing.rcId !== incoming.rcId) ||
      (!(existing.id && incoming.id) && existing.name && incoming.name && key(existing.name) !== key(incoming.name))) {
    return { ...record, companyDetails: { ...existing, identityConflict: true } };
  }
  return { ...record, companyDetails: Object.fromEntries(Object.entries(existing).map(([field, value]) =>
    [field, value == null || value === "" || (Array.isArray(value) && !value.length) ? incoming[field] ?? value : value],
  )) };
}

const aiIndustryKeys = new Set(["ai", "artificial intelligence", "ml", "machine learning", "deep learning"]);

export function getRequestedCompanyKeywords(query = "", industries = []) {
  // "AI developer" describes a person's skill, not necessarily their employer.
  const industryValues = Array.isArray(industries) ? industries : [industries];
  return industryValues.some((industry) => aiIndustryKeys.has(key(industry))) ||
    /\b(?:ai|artificial intelligence|machine learning|ml)(?:[\s-]+(?:powered|focused|driven|software|saas|tech|technology|early[\s-]+stage)){0,2}[\s-]+(?:startups?|companies|company|firms?|business(?:es)?)\b/i.test(query)
    ? ["AI"] : [];
}

const industryAliases = {
  "computer software": "software development",
  software: "software development",
  technology: "technology information and media",
  tech: "technology information and media",
  "information technology and services": "it services and it consulting",
  "information technology services": "it services and it consulting",
  healthcare: "hospitals and health care",
  "health care": "hospitals and health care",
  "hospital and health care": "hospitals and health care",
};
const industryKey = (value) => aiIndustryKeys.has(key(value))
  ? "technology information and media" : industryAliases[key(value)] || key(value);
const technologyIndustries = new Set([
  "technology information and media", "technology information and internet",
  "software development", "it services and it consulting", "it system custom software development",
  "computer hardware manufacturing", "computer networking products", "telecommunications",
  "data infrastructure and analytics", "internet publishing", "computer and network security",
  "semiconductor manufacturing", "wireless services", "information services",
]);

export function matchesCompanyIndustry(actual, requested) {
  const expected = industryKey(requested);
  const industry = industryKey(actual);
  return industry === expected || (expected === "technology information and media" && technologyIndustries.has(industry));
}

export function hasCompanyCriteria(filters = {}, query = "") {
  return Boolean(filters.currentCompanyIndustry?.length || filters.currentCompanyEmployeeCountRange ||
    (filters.companyKeywords ?? getRequestedCompanyKeywords(query, filters.currentCompanyIndustry)).length);
}
